import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, Cloud, CloudRain, Sun, FileCheck, Thermometer } from "lucide-react"
import "./Visa.css"

const Visa = ({ countryData }) => {
  const [activeVisaIndex, setActiveVisaIndex] = useState(0)
  const autoPlayRef = useRef(null)

  const visaTypes = countryData?.visa?.types || [
    {
      title: "Short-term Study",
      cost: "£ 94",
      type: "Type- Study",
      description: "Ideal for short-term language courses or vocational training under 6 months.",
    },
    {
      title: "General Student - Tier 4",
      cost: "£ 339",
      type: "Type- Study",
      description: "For students pursuing post-16 education at a licensed sponsor institution.",
    }
  ]

  const weatherData = countryData?.weather || [
    { city: "London", min: "10°", max: "18°" },
    { city: "Manchester", min: "9°", max: "15°" },
    { city: "Birmingham", min: "7°", max: "16°" },
    { city: "Glasgow", min: "8°", max: "15°" },
  ]

  useEffect(() => {
    autoPlayRef.current = setTimeout(() => {
      setActiveVisaIndex((prev) => (prev === visaTypes.length - 1 ? 0 : prev + 1))
    }, 6000)
    return () => clearTimeout(autoPlayRef.current)
  }, [activeVisaIndex, visaTypes.length])

  const handlePrev = () => {
    clearTimeout(autoPlayRef.current)
    setActiveVisaIndex((prev) => (prev === 0 ? visaTypes.length - 1 : prev - 1))
  }

  const handleNext = () => {
    clearTimeout(autoPlayRef.current)
    setActiveVisaIndex((prev) => (prev === visaTypes.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="post-admission-container">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        Post Admission Experience
      </motion.h1>

      <div className="visa-weather-grid">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="visa-section"
        >
          <h2><FileCheck size={28} color="#a855f7" /> Visa for {countryData?.name}</h2>

          <div className="visa-slider-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeVisaIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="visa-card"
              >
                <h3>{visaTypes[activeVisaIndex].title}</h3>
                <div className="visa-meta">
                  <div className="visa-meta-item">Cost: <strong>{visaTypes[activeVisaIndex].cost}</strong></div>
                  <div className="visa-meta-item">Type: <strong>{visaTypes[activeVisaIndex].type}</strong></div>
                </div>
                <p className="visa-description">{visaTypes[activeVisaIndex].description}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="slider-controls">
            <button className="slider-btn" onClick={handlePrev}><ChevronLeft /></button>
            <button className="slider-btn" onClick={handleNext}><ChevronRight /></button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="weather-section"
        >
          <h2><Thermometer size={28} color="#06b6d4" /> Weather</h2>

          <div className="weather-grid">
            {weatherData.map((city, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
                className="weather-card"
              >
                <div className="city-name">{city.city}</div>
                <div className="temp-grid">
                  <div className="temp-item">
                    <span className="temp-label">Min</span>
                    <span className="temp-value min">{city.min}</span>
                  </div>
                  <div className="temp-item">
                    <span className="temp-label">Max</span>
                    <span className="temp-value max">{city.max}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Visa
